// driverDay.js
// -----------------------------------------------------------------------------
// What a driver's day is, as a pure function of the rows already in Firestore.
//
// `deliveryList` holds one document per SO/RT, but a STOP is a customer visit
// and may span several of them. timestamp.vue reconstructed stops by grouping on
// custName in the browser and stamped every underlying document identically.
// Two timestamps survive that; four timestamps plus one signature and one rating
// per visit do not -- hence a stop as a thing, built here.
//
// Pure: no Vue, no Firestore, CommonJS, so scripts/check-driver-day.js can
// require() it. The same arrangement bookingRules.js and dispatchPlan.js keep.
// -----------------------------------------------------------------------------

const R = require('./bookingRules')
const geo = require('./geo')
const travelTime = require('./travelTime')
const installTime = require('./installTime')
const RJ = require('./returnJob')
const DR = require('./deliveryRow')

// The four stamps, in the only order they may be recorded.
//
// `departed` means "leaving FOR this stop" -- from the depot for the first stop,
// from the previous customer thereafter. That is what makes a leg time a leg
// time later, rather than an unattributable gap.
const STEPS = ['departed', 'arrived', 'installStarted', 'finished']

// One status per prefix of STEPS. Index i is the status after i stamps --
// except the last, 'failed', which no prefix reaches: it is the status of a
// stop closed by a `rejected` stamp (see REJECTED). It sits after 'done' so
// every "has the driver reached the customer" test written as
// `indexOf(status) >= indexOf('arrived')` stays true for it.
const STATUSES = ['pending', 'enroute', 'arrived', 'installing', 'done', 'failed']

// ส่งไม่สำเร็จ: the customer did not accept the delivery. Not one of STEPS,
// because it is not the next rung of the ladder but a way off it -- taken any
// time after ถึงบ้านลูกค้า and before เสร็จงาน, and it closes the stop. Its
// reasons and photos live on deliveryStop.proof; see deliveryOutcome.js.
const REJECTED = 'rejected'

function isStamped (steps, step) {
  return !!(steps && steps[step] && steps[step].at != null)
}

// รอปิดงาน: the customer has saved their page of ปิดงาน (signature and
// rating, proof.capturedAt) but the driver has not yet photographed the receipt
// and closed the job. Read from the saved proof, not from anything on the
// phone, so the stop screen, the day list and the monitor board agree.
function awaitingClose (stop) {
  const s = stop || {}
  return statusFor(s.steps) === 'installing' && !!(s.proof && s.proof.capturedAt)
}

// A stop the driver is finished with, delivered or not: nothing left to stamp,
// nothing left to predict, and it no longer counts as work remaining.
function isClosed (status) {
  return status === 'done' || status === 'failed'
}

// A Firestore document id may not contain '/', may not be '.' or '..', and is
// nicer to read without runs of whitespace. custName reaches an id through the
// stop-key fallback and is free text out of SAP, so it is sanitised rather than
// trusted.
function safeIdPart (str) {
  const s = String(str == null ? '' : str).replace(/\//g, '_').trim().replace(/\s+/g, '_')
  if (!s || s === '.' || s === '..') return '_'
  return s
}

// What makes two rows the same visit.
//
// The telephone, normalised exactly as bookingRules does it so that '081-234-5678',
// '0812345678' and '+66812345678' are one customer. Name is the fallback and not
// the default: SAP writes the same name for a shop's every branch.
function stopKeyFor (row) {
  const r = row || {}
  const tel = R.normalizeTel(r.custTel)
  if (tel) return tel
  return safeIdPart(r.custName)
}

// The first row's `field` that is non-blank and, when `valid` is given, passes it.
// Rows arrive in the deterministic id order groupStops sorts them into.
function firstFilled (rows, field, valid) {
  for (let i = 0; i < rows.length; i++) {
    const v = String(rows[i][field] || '').trim()
    if (v && (!valid || valid(v))) return v
  }
  return ''
}

function stopIdFor (deliveryDate, regNum, stopKey) {
  return [safeIdPart(deliveryDate), safeIdPart(regNum), safeIdPart(stopKey)].join('_')
}

// '13:00' -> 780, and null for anything else. Strict on purpose: the tolerant
// prefix match this replaced read '08:00 - 12:00' as plain 08:00, which is how a
// window silently became an instant everywhere it was used.
function clockMinutes (text) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(text == null ? '' : text).trim())
  if (!m) return null
  const h = Number(m[1])
  const min = Number(m[2])
  if (h > 23 || min > 59) return null
  return h * 60 + min
}

// A slot named rather than spelled out to the hours it stands for. All three
// spellings reach a deliveryList row: carBooking's _prepareTime() writes
// 'AM'/'PM' uppercase, bookingRules names its slots lowercase, and staff see
// them in Thai. Taken from R.SLOT_TIME rather than restated, so the hours a slot
// means are defined in exactly one place.
function slotRange (text) {
  const key = text.toLowerCase()
  if (R.SLOT_TIME[key]) return R.SLOT_TIME[key]
  const keys = Object.keys(R.SLOT_LABEL_TH)
  for (let i = 0; i < keys.length; i++) {
    if (R.SLOT_LABEL_TH[keys[i]] === text) return R.SLOT_TIME[keys[i]]
  }
  return null
}

/**
 * A promised time as the WINDOW it actually is.
 *
 * `timeUpdate` holds whatever carBooking.vue's _prepareTime() returned -- a
 * range ('08:00 - 12:00'), a slot ('AM', 'ทั้งวัน'), or occasionally a bare
 * clock. Reading only a leading 'HH:mm', as this module did, sorted every
 * slot-named stop past the end of the day and left the ETA chain unable to
 * restart on one at all.
 *
 * BOTH ENDS ARE REPORTED, because they answer different questions:
 *   start -- the earliest the driver can arrive. Nobody turns up before the
 *            window opens, so a broken chain restarts here.
 *   end   -- the deadline the customer was given. The day is ordered by it, and
 *            lateness is measured against it.
 *
 * Unreadable stays null at both ends rather than guessing: a fabricated promise
 * anchors a whole day's ETAs on a number nobody committed to.
 */
function promisedWindow (promised) {
  const empty = { start: null, end: null }
  if (typeof promised !== 'string') return empty
  const text = promised.trim()
  if (!text) return empty
  const range = slotRange(text) || text
  const parts = range.split('-')
  if (parts.length === 2) {
    const start = clockMinutes(parts[0])
    const end = clockMinutes(parts[1])
    if (start == null || end == null) return empty
    return { start: start, end: end }
  }
  const at = clockMinutes(range)
  return at == null ? empty : { start: at, end: at }
}

// The key the day is ordered by: the DEADLINE. Anything unreadable sorts past
// the end of the day, where it is visible rather than silently first.
function sortableTime (hhmm) {
  const end = promisedWindow(hhmm).end
  return end == null ? 24 * 60 + 1 : end
}

// The tiebreak among stops sharing a deadline: whichever window OPENS first. A
// ทั้งวัน job and a บ่าย job both end at 17:00, but only one of them can be
// delivered in the morning.
function openingTime (hhmm) {
  const start = promisedWindow(hhmm).start
  return start == null ? 24 * 60 + 1 : start
}

/**
 * Undoes what the ID token decoder does to a non-ASCII custom claim.
 *
 * firebase@6.6.2 decodes the token payload byte-wise -- base64 to a binary
 * string, with no UTF-8 step -- so a claim holding Thai arrives as its UTF-8
 * BYTES, one per code point: 'ศ' (U+0E28 = e0 b8 a8) comes back as three
 * characters, 'à', '¸', '¨'. The driver app then stamped that onto every
 * deliveryStop it created, which is why the monitor board showed driver names as
 * à¸¨à¸¸à¸ . The server-written copies in `driver` and `driverAuth` were never
 * affected; only what a CLIENT read out of the token was.
 *
 * The right fix would be in the SDK, which we do not control on this version, so
 * it is undone at the one place the claim is read (driverService's
 * ensureDriverClaims).
 *
 * SAFE TO RUN TWICE, and over text that was never broken -- which matters
 * because the repair script for the already-stamped documents uses this same
 * function. Real Thai sits far above 0xff and so can never be mistaken for a
 * byte string, and a byte sequence that is not valid UTF-8 is left exactly as it
 * was rather than guessed at.
 */
function repairClaimText (value) {
  if (typeof value !== 'string' || !value) return value
  let percent = ''
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i)
    // Thai sits far above 0xff, so anything up here is already correct text and
    // must be left exactly as it is.
    if (code > 0xff) return value
    percent += '%' + ('0' + code.toString(16)).slice(-2)
  }
  try {
    return decodeURIComponent(percent)
  } catch (e) {
    // Not valid UTF-8 -- so it was never mangled Thai in the first place.
    return value
  }
}

// A stamp's `atDevice` is written as new Date().toISOString() -- UTC. Thailand is
// UTC+7 and has never observed DST, so a fixed offset converts one exactly, and
// keeps this module free of moment-timezone -- which is what lets
// scripts/check-driver-day.js require() it. stop.vue formats the same field with
// moment.tz and must agree; both render Asia/Bangkok.
const TZ_OFFSET_MINUTES = 7 * 60

// Minutes since Bangkok midnight, or null for anything unreadable. Null rather
// than a guess: a stamp that cannot be read must fall back to the forecast, not
// silently anchor the whole day at 00:00.
function minutesFromISO (iso) {
  if (!iso) return null
  const t = Date.parse(iso)
  if (isNaN(t)) return null
  const shifted = new Date(t + TZ_OFFSET_MINUTES * 60000)
  return shifted.getUTCHours() * 60 + shifted.getUTCMinutes()
}

function clockFromISO (iso) {
  const m = minutesFromISO(iso)
  return m == null ? '' : travelTime.toClock(m)
}

function stampMinutes (steps, step) {
  const s = steps && steps[step]
  return s ? minutesFromISO(s.atDevice) : null
}

/**
 * Rows of one day for one vehicle -> ordered stops.
 *
 * Ordering is by `timeUpdate`, the time the office promised the customer, and
 * `seq` is 1-based. Input order does not affect the result: lines within a stop
 * are sorted by their document id and descriptive fields are taken from the
 * canonical line (the one with the lowest deliveryListId), so the same day
 * always produces the same ids, `lines` order, and all descriptive fields,
 * whatever order Firestore returned them in. `promisedTime` is the earliest
 * across all lines; `itemRows` are pooled from all lines.
 */
function groupStops (rows) {
  const byKey = {}
  const order = []

  // Collect rows by key, preserving key insertion order
  ;(rows || []).forEach(row => {
    if (!row) return
    const key = stopKeyFor(row)
    if (!byKey[key]) {
      byKey[key] = []
      order.push(key)
    }
    byKey[key].push(row)
  })

  const stops = order.map(key => {
    const rowsForKey = byKey[key]
    // Sort rows by deliveryListId to get deterministic canonical row
    rowsForKey.sort((a, b) => String(a.id || '').localeCompare(String(b.id || '')))
    const canonical = rowsForKey[0]

    // Descriptive fields from canonical row
    const stop = {
      stopKey: key,
      deliveryDate: canonical.deliveryDate || '',
      regNum: canonical.regNum || '',
      custName: canonical.custName || '',
      custTel: canonical.custTel || '',
      custAddress: canonical.custAddress || '',
      // The พิกัด staff confirmed with the customer, when the booking carried
      // one. It is the only location this app may navigate to directly; see
      // locateStop and mapsUrl.
      //
      // Taken from the first row that HAS one, not from the canonical row: a
      // customer with two SOs is two rows, and a pin typed onto one of them in
      // carBooking's slot dialog -- or carried by only one of the two bookings --
      // was lost whenever the other row happened to sort first.
      geo: firstFilled(rowsForKey, 'geo', R.parseGeo),
      // จุดสังเกต. Not a location the app can compute with -- free text a
      // person wrote for another person -- so it is carried and shown, never
      // resolved. First non-blank, for the same reason as geo.
      landmark: firstFilled(rowsForKey, 'landmark'),
      cardCode: canonical.cardCode || '',
      cardName: canonical.cardName || '',
      cardPhone: canonical.cardPhone || '',
      promisedTime: canonical.timeUpdate || canonical.time || '',
      // Multi-day trips (deliveryRow): the departure day is the date; these say
      // which day the plan expects the drop, its place in the plan, and the last
      // day the trip may still deliver.
      transNo: canonical.transNo || '',
      tripEnd: rowsForKey.map(r => r.tripEnd).filter(Boolean).sort().slice(-1)[0] || '',
      planDate: rowsForKey.map(r => r.planDate).filter(Boolean).sort()[0] || '',
      planSeq: rowsForKey.map(r => r.planSeq).filter(n => n != null).sort((a, b) => a - b)[0],
      lines: [],
      itemRows: []
    }

    // Collect all lines and compute earliest promised time
    rowsForKey.forEach(row => {
      stop.lines.push({
        deliveryListId: row.id || '',
        soNo: row.soNo || row.rtNo || '',
        rtNo: row.rtNo || '',
        timelineId: row.timelineId || '',
        // What this SO puts on the truck, for the driver to check against
        // what was loaded. See productsFor.
        products: productsFor(row.prodList),
        // What the driver collects at this stop (an exchange, or a pickup).
        returns: RJ.returnLines(row.prodListRt)
      })
      if (Array.isArray(row.prodList)) stop.itemRows = stop.itemRows.concat(row.prodList)
      // The earliest promised time across the lines is the one the customer was
      // given; a later line must not push the visit down the day.
      if (sortableTime(row.timeUpdate) < sortableTime(stop.promisedTime)) {
        stop.promisedTime = row.timeUpdate
      }
    })

    stop.returnOnly = rowsForKey.every(r => r.status === 'RT')
    // Actual lines to collect, not a copied rtNo: an SO row written before
    // returns existed can carry its RT's number with an empty prodListRt, and
    // must not be charged the return's 30 minutes for it.
    stop.hasReturn = stop.lines.some(l => l.returns.length > 0)

    stop.id = stopIdFor(stop.deliveryDate, stop.regNum, stop.stopKey)
    return stop
  })

  stops.sort((a, b) => {
    // A planned trip keeps the plan's order (deliveryRow's planSeq).
    if (a.planSeq != null && b.planSeq != null && a.planSeq !== b.planSeq) return a.planSeq - b.planSeq
    const d = sortableTime(a.promisedTime) - sortableTime(b.promisedTime)
    if (d !== 0) return d
    // Same deadline: the window that opens first goes first.
    const o = openingTime(a.promisedTime) - openingTime(b.promisedTime)
    if (o !== 0) return o
    // A stable tiebreak, so two stops promised the same time do not swap places
    // between one load and the next.
    return String(a.id).localeCompare(String(b.id))
  })

  stops.forEach((s, i) => { s.seq = i + 1 })

  return stops
}

// The status is the length of the unbroken prefix of STEPS that has been
// stamped. A stamp recorded past a gap does not promote the stop: if `arrived`
// somehow exists without `departed`, the stop is still waiting to depart.
function statusFor (steps) {
  const s = steps || {}
  // A rejection counts only on a stop that was reached and not finished --
  // the same window canStamp allows it in -- so a stray field cannot turn a
  // delivered job into a failed one.
  if (isStamped(s, REJECTED) && isStamped(s, 'departed') && isStamped(s, 'arrived') && !isStamped(s, 'finished')) {
    return 'failed'
  }
  let n = 0
  for (let i = 0; i < STEPS.length; i++) {
    if (s[STEPS[i]] && s[STEPS[i]].at != null) n++
    else break
  }
  return STATUSES[n]
}

/**
 * What a `deliveryStop` document says about work already done, for the screens
 * that DELETE a job.
 *
 * Deleting a postponed job is right and stays right -- including after
 * `departed` or `arrived`, because a customer who is not home and reschedules
 * should leave the driver's list. Deleting a job the driver has already
 * FINISHED is a different act: the delivery happened, and the delete destroys
 * the `deliveryList` row and its `deliveryTimeline` document, leaving no record
 * of the visit anywhere except an orphaned `deliveryStop` that nothing reads.
 * On 2026-09-09 that happened once, in testing.
 *
 * So this does not refuse anything -- dispatch keeps the last word -- it only
 * tells the delete screens what they are about to erase. managecar.vue's four
 * delete sites and carBooking.vue's `deleteBooking` all ask before their
 * confirm dialog opens; driverService.stampGuardForListDoc is the reader.
 *
 * `stamped` is ANY step carrying a server `at`, not `statusFor`'s prefix: a
 * ladder with a hole in it still means a driver touched this job, and the
 * warning is exactly as warranted there. `at` and `atDevice` are written
 * together by stampStep, so a step with a null `at` is a partial write and not
 * a stamp. `lastAt` is the phone's own clock, which is the only one this
 * module can read -- unreadable simply shows no time rather than suppressing
 * the warning.
 */
function deleteGuard (stopDoc) {
  const steps = (stopDoc && stopDoc.steps) || {}
  let lastStep = null
  let stamped = false
  STEPS.forEach(step => {
    if (steps[step] && steps[step].at != null) {
      stamped = true
      lastStep = step
    }
  })
  // A rejection is the last thing that happened on a failed stop.
  if (isStamped(steps, REJECTED)) {
    stamped = true
    lastStep = REJECTED
  }
  return {
    stamped: stamped,
    finished: !!(steps.finished && steps.finished.at != null),
    status: statusFor(steps),
    lastStep: lastStep,
    lastAt: lastStep ? clockFromISO(steps[lastStep].atDevice) : ''
  }
}

// What the staff screens say about a stamped job, in one place because two of
// them say it: managecar.vue's four delete dialogs and carBooking.vue's
// deleteBooking confirm. The same Thai wording either way -- the act being
// warned about is the same act.
const GUARD_LABELS = {
  pending: 'ยังไม่ออก', enroute: 'กำลังไป', arrived: 'ถึงแล้ว',
  installing: 'กำลังติดตั้ง', done: 'เสร็จแล้ว', failed: 'ส่งไม่สำเร็จ'
}

/**
 * deleteGuard(...) -> the one line the delete dialog adds, or '' for a job no
 * driver has touched (the overwhelming majority, which must stay a plain
 * confirm -- a warning on every delete is a warning nobody reads).
 *
 * A guard that could not be read says so rather than staying silent: "no
 * warning" and "not checked" mean different things to whoever is about to
 * press ตกลง.
 */
function deleteGuardNote (guard) {
  const g = guard || {}
  if (g.known === false) return 'ตรวจสอบสถานะจากแอปคนขับไม่ได้ — กรุณาตรวจสอบก่อนลบ'
  if (!g.stamped) return ''
  const at = g.lastAt ? ' เมื่อ ' + g.lastAt + ' น.' : ''
  if (g.finished) {
    return 'คนขับส่งงานนี้เสร็จแล้ว' + at + ' — ลบแล้วจะไม่เหลือประวัติการส่งงานนี้ ยืนยันลบ?'
  }
  // Rebooking a rejected job is the ordinary next step, so it is a plain note
  // and never the red "the delivery happened" warning above.
  if (g.status === 'failed') return 'คนขับแจ้งส่งไม่สำเร็จ' + at + ' — ยืนยันลบ?'
  return 'คนขับเริ่มงานนี้แล้ว (' + (GUARD_LABELS[g.status] || GUARD_LABELS.pending) + ')' + at + ' — ยืนยันลบ?'
}

function nextStep (stop) {
  const s = (stop && stop.steps) || {}
  if (statusFor(s) === 'failed') return null
  for (let i = 0; i < STEPS.length; i++) {
    if (!s[STEPS[i]] || s[STEPS[i]].at == null) return STEPS[i]
  }
  return null
}

// Refuses an unknown step, a step already recorded, and a step out of order.
// This is enforced here rather than only in the button label, because Firestore
// rules are open -- this module is the last place that can say no.
function canStamp (stop, step) {
  if (step === REJECTED) {
    const status = statusFor((stop && stop.steps) || {})
    return status === 'arrived' || status === 'installing'
  }
  if (STEPS.indexOf(step) === -1) return false
  return nextStep(stop) === step
}

// When the truck leaves the depot. Loading happens the evening before, so the
// day starts with driving.
const DEFAULT_START = '08:00'

// The shortest a leg between two stops can be, whatever the map says.
//
// deliveryList carries no dropped pin, so geo resolves an address to a district
// or subdistrict CENTROID -- two customers in the same เขต land on the SAME
// point, and travelMinutes then correctly reports 0, because a leg of no
// distance takes no driving. The drive is not the whole gap: the driver packs
// up, walks to the truck, drives, parks again and gets to the next door. Zero is
// never the truth, and on a real day it surfaced as stop 2 predicted to the
// exact minute stop 1 finished.
//
// A floor, not an addition: it can only ever raise a leg, so a measured 50km run
// from the depot is untouched.
const MIN_LEG_MINUTES = 15

function legMinutes (from, to, at) {
  const m = travelTime.travelMinutes(from, to, at)
  return m == null ? null : Math.max(m, MIN_LEG_MINUTES)
}

// SAP product rows (prodList, as copied onto deliveryList when the job was
// created) -> [{ code, name, qty }], for a driver to read.
//
// Read through installTime's productName / productQty, the same readers the
// install duration and the booking form count with, so the list a driver sees
// cannot add up to a different number from the one staff booked. A line SAP
// gave no quantity is one of it, as itemsFromRows counts it. A row with no
// readable name is dropped: an entry reading "× 2" tells a driver nothing.
//
// This is a snapshot. An SO edited in SAP after the job was created is not
// reflected, which is why the views say "ตามข้อมูล ณ วันที่จัดรถ".
function productsFor (rows) {
  if (!Array.isArray(rows)) return []
  return rows.map(row => ({
    code: String((row && row.prodCode) || '').trim(),
    name: installTime.productName(row),
    qty: installTime.productQty(row) || 1
  })).filter(p => p.name)
}

// Everything the given stops put on the truck, one entry per product, in the
// order a product is first met along the route. Merged by SAP code when there
// is one and by name when there is not, so the same item on two SOs is one line
// the driver counts once. `stops` lists each stop's `seq` it goes to, so a
// driver short of an item knows which customer it is for.
function loadList (stops) {
  const byKey = {}
  const order = []
  const list = stops || []
  list.forEach(stop => {
    const lines = (stop && stop.lines) || []
    lines.forEach(line => {
      const products = line.products || []
      products.forEach(p => {
        const key = p.code ? 'c:' + p.code : 'n:' + p.name
        if (!byKey[key]) {
          byKey[key] = { code: p.code, name: p.name, qty: 0, stops: [] }
          order.push(key)
        }
        const entry = byKey[key]
        entry.qty += p.qty
        if (stop.seq != null && entry.stops.indexOf(stop.seq) < 0) entry.stops.push(stop.seq)
      })
    })
  })
  return order.map(key => byKey[key])
}

// The พิกัด first, the address only as a fallback.
//
// A `geo` on the row is a pin staff dropped after asking the customer, so it is
// the one location worth navigating to directly -- resolveBooking reports it as
// 'exact', and mapsUrl sends coordinates only at that precision. A row without
// one resolves through the free-text address and reports 'subdistrict',
// 'district' or 'zipcode': a clustering hint for the ETA chain, never a
// navigation target. resolveBooking is given the shape it expects.
function locateStop (stop) {
  const s = stop || {}
  return geo.resolveBooking({ geo: s.geo || '', address: s.custAddress || '' })
}

// The pooled product rows of every SO line in the visit.
//
// Note the two-step: minutesForBooking reads `itemCount` / `items` /
// `productList`, and knows nothing about SAP's `prodList` ROWS -- handing it the
// rows directly would silently fall through to the default tier. itemsFromRows
// is the reader for those rows, and is what the booking form and the edit dialog
// already count with, so a stop's duration cannot disagree with the number staff
// saw when they booked it.
function installMinutesFor (stop) {
  const rows = (stop && stop.itemRows) || []
  if (stop && stop.returnOnly) return installTime.minutesForBooking({ jobType: 'return' })
  const items = installTime.itemsFromRows(rows)
  const base = items > 0 ? { itemCount: items } : {}
  if (stop && stop.hasReturn) base.jobType = 'exchange'
  return installTime.minutesForBooking(base)
}

// How a line is named to the driver. A return row's line carries its RT in
// soNo (groupStops: soNo || rtNo), so it must not be printed under "SO".
function lineRef (line) {
  const l = line || {}
  const so = String(l.soNo || '')
  const rt = String(l.rtNo || '')
  if (rt && so === rt) return 'RT ' + rt
  if (rt && l.returns && l.returns.length) return 'SO ' + so + ' · RT ' + rt
  return so ? 'SO ' + so : ''
}

const STAMP_LABEL = { departed: 'ออกเดินทาง', arrived: 'ถึงบ้านลูกค้า', installStarted: 'เริ่มติดตั้ง', finished: 'เสร็จงาน' }
// A pickup installs nothing: its third step is collecting the items.
function stampLabel (stop, step) {
  if (step === 'installStarted' && stop && stop.returnOnly) return 'เริ่มรับคืน'
  return STAMP_LABEL[step] || ''
}

/**
 * Hangs an ETA off each stop, in order.
 *
 * The chain is: depot -> stop 1 -> stop 2 ... Each leg is measured with
 * travelTime, which applies the traffic bands band by band, and each stop then
 * consumes its install duration before the next departure.
 *
 * When a leg cannot be measured -- an address that resolves nowhere -- the chain
 * does NOT invent one. That stop's `eta` is null. The driver still has to do the
 * work (installMinutes), so the 'floor' (earliest the driver could be free)
 * advances by that duration but NOT by travel time, which is unknown.
 *
 * The next stop restarts the chain from its own `promisedTime` floored at the
 * driver's known-free time: `clock = max(promisedTime, floor)`. If the floor is
 * unknown (the chain broke on the very first stop, before anything was measured),
 * promisedTime alone is used. The restart is marked `etaSource: 'promised'` so
 * the screen can show it as committed-but-unverified. If the restart itself fails
 * (promisedTime is missing or unparseable), the chain stays broken so the next
 * stop gets its own restart opportunity.
 */
function planStops (stops, options) {
  const opts = options || {}
  const startAt = opts.startAt || DEFAULT_START
  const list = (stops || []).slice()

  let from = opts.from || geo.DEPOT
  let clock = travelTime.toMinutes(startAt)
  let floor = clock // Earliest the driver could be free, even if unplaceable
  let chainBroken = false

  list.forEach(stop => {
    stop.location = locateStop(stop)
    const install = installMinutesFor(stop)
    stop.installMinutes = install.minutes
    stop.installSource = install.source

    // What the driver's own stamps say, before anything is predicted.
    //
    // Read through statusFor rather than off the raw steps, so the times shown
    // and the status badge can never disagree: an `arrived` recorded past a gap
    // does not promote the stop, and must not produce a "ถึงจริง" either.
    const steps = stop.steps || {}
    const status = statusFor(steps)
    const reached = STATUSES.indexOf(status) >= STATUSES.indexOf('arrived')
    const arrivedAt = reached ? stampMinutes(steps, 'arrived') : null
    const finishedAt = isClosed(status)
      ? stampMinutes(steps, status === 'done' ? 'finished' : REJECTED)
      : null

    stop.actualArrive = arrivedAt == null ? '' : travelTime.toClock(arrivedAt)
    stop.actualFinish = finishedAt == null ? '' : travelTime.toClock(finishedAt)

    // The driver has been here. Nothing about this stop is a forecast any more,
    // and the chain past it restarts from when they were (or will be) free --
    // their finish time, or their arrival plus the install still to run.
    const freeAt = finishedAt != null
      ? finishedAt
      : (arrivedAt == null ? null : arrivedAt + stop.installMinutes)
    if (reached && freeAt != null) {
      stop.eta = null
      stop.etaClock = ''
      stop.etaSource = 'actual'
      clock = freeAt
      floor = freeAt
      if (geo.isPoint(stop.location)) from = stop.location
      // Where the driver is and when is known again, whatever happened earlier.
      chainBroken = false
      return
    }

    // Left for this stop but not arrived: the leg is real, its length is still a
    // prediction -- but one measured from a departure that actually happened
    // rather than from the depot at DEFAULT_START.
    const departedAt = status === 'enroute' ? stampMinutes(steps, 'departed') : null
    if (departedAt != null) {
      const leg = geo.isPoint(stop.location)
        ? legMinutes(from, stop.location, departedAt)
        : null
      if (leg == null) {
        stop.eta = null
        stop.etaClock = ''
        stop.etaSource = null
        // The driver is on their way and will still do the work; only the
        // travel is unknown.
        floor = departedAt + stop.installMinutes
        clock = null
        chainBroken = true
        return
      }
      clock = departedAt + leg
      stop.eta = clock
      stop.etaClock = travelTime.toClock(clock)
      stop.etaSource = 'enroute'
      clock += stop.installMinutes
      floor = clock
      from = stop.location
      chainBroken = false
      return
    }

    if (chainBroken) {
      // The window's START: the driver cannot arrive before it opens, and
      // restarting at its END would push every recovered stop to the last
      // minute the customer would accept.
      const promised = promisedWindow(stop.promisedTime).start
      // The driver can't arrive before the floor (latest they could be free)
      // or before their promised time, whichever is later.
      const restartClock = promised == null ? null : Math.max(promised, floor == null ? 0 : floor)
      clock = restartClock
      from = geo.isPoint(stop.location) ? stop.location : from
      chainBroken = (clock == null) // Only successful restarts clear the flag
      stop.eta = clock
      stop.etaClock = clock == null ? '' : travelTime.toClock(clock)
      stop.etaSource = clock == null ? null : 'promised'
      if (clock != null) {
        clock += stop.installMinutes
        floor = clock
      }
      return
    }

    const minutes = geo.isPoint(stop.location) && clock != null
      ? legMinutes(from, stop.location, clock)
      : null

    if (minutes == null) {
      stop.eta = null
      stop.etaClock = ''
      stop.etaSource = null
      // Even though we can't measure the travel time, the driver still has to do
      // the work at this stop. Advance the floor by install duration only.
      floor = floor == null ? null : floor + stop.installMinutes
      chainBroken = true
      return
    }

    clock = clock + minutes
    stop.eta = clock
    stop.etaClock = travelTime.toClock(clock)
    stop.etaSource = 'chain'
    clock += stop.installMinutes
    floor = clock
    from = stop.location
  })

  return list
}

/**
 * The นำทาง link.
 *
 * Coordinates ONLY when a pin was actually dropped. geo.js reports precision
 * precisely so callers do not make this mistake: a zipcode can span 25 ตำบล, so
 * its centroid is a clustering hint, never a navigation target. Sending a driver
 * confidently to the wrong village is worse than making Google geocode the line
 * the customer gave.
 *
 * The https form is used rather than comgooglemaps:// so that an iPhone without
 * Google Maps installed degrades to Google Maps in the browser instead of doing
 * nothing at all.
 */
function mapsUrl (stop) {
  const s = stop || {}
  const base = 'https://www.google.com/maps/dir/?api=1&travelmode=driving&destination='
  if (s.location && s.location.precision === 'exact' && geo.isPoint(s.location)) {
    return base + encodeURIComponent(s.location.lat + ',' + s.location.lng)
  }
  const text = String(s.custAddress || '').trim()
  if (!text) return ''
  return base + encodeURIComponent(text)
}

// Rows of several dates (a trip's departure day beside today's work) -> stops,
// each date grouped on its own: one customer on two dates is two visits with
// two ids, never one stop with the wrong date. seq runs across the result.
function groupStopsByDate (rows) {
  const dates = []
  const by = {}
  ;(rows || []).forEach(r => {
    if (!r) return
    const d = r.deliveryDate || ''
    if (!by[d]) { by[d] = []; dates.push(d) }
    by[d].push(r)
  })
  dates.sort()
  const out = []
  dates.forEach(d => groupStops(by[d]).forEach(s => out.push(s)))
  out.forEach((s, i) => { s.seq = i + 1 })
  return out
}

function isTripStop (stop) {
  return !!(stop && stop.tripEnd && stop.deliveryDate && stop.tripEnd > stop.deliveryDate)
}

function tripInfo (stop, today) {
  if (!isTripStop(stop)) return null
  return { date: stop.deliveryDate, tripEnd: stop.tripEnd, dayNo: DR.daysBetween(stop.deliveryDate, today) + 1, days: DR.daysBetween(stop.deliveryDate, stop.tripEnd) + 1 }
}

// The Bangkok day a closed stop was closed on (finished, or rejected), '' when open.
function closedDay (stop) {
  const s = stop || {}
  if (!isClosed(s.status)) return ''
  const step = (s.steps || {})[s.status === 'failed' ? REJECTED : 'finished']
  const t = step && Date.parse(step.atDevice)
  if (!t) return ''
  return new Date(t + TZ_OFFSET_MINUTES * 60000).toISOString().slice(0, 10)
}

// The trips still running on `today`, from the trip documents -- which carry no
// customer data, so a driver's phone never loads another truck's customers to
// find its own trip. Up-country: sent with a number, left by today, not past
// its return day (plus `overdueDays` for the monitor). Small car: a sent,
// multi-day route that left before today and has not ended.
function openTripKeys ({ upTrips, routes, today, overdueDays }) {
  const out = []
  const key = (kind, id, date, regNum, tripEnd) => ({
    kind, id, date, regNum, tripEnd,
    dayNo: DR.daysBetween(date, today) + 1,
    days: DR.daysBetween(date, tripEnd) + 1,
    overdue: today > tripEnd
  })
  ;(upTrips || []).forEach(t => {
    if (!t || t.status !== 'sent' || !t.transNo || !t.regNum || !t.startDate || !t.returnDate) return
    if (t.startDate > today || today > DR.addDays(t.returnDate, overdueDays || 0)) return
    out.push(key('up', t.id, t.startDate, t.regNum, t.returnDate))
  })
  ;(routes || []).forEach(r => {
    if (!r || r.cancelled || !r.sentAt || !r.regNum || !r.date) return
    const days = Number(r.days) || 1
    if (days < 2) return
    const end = DR.addDays(r.date, days - 1)
    if (r.date > today || today > end) return
    out.push(key('route', r.id, r.date, r.regNum, end))
  })
  return out
}

// day.vue's sections. A plate's ordinary jobs are 'plain'; a trip's stops are
// split by the plan's day -- and any pending stop stays stampable whatever its
// section: nothing enforces the plan's day or order.
function tripSections (stops, today) {
  const by = { plain: [], today: [], carried: [], pending: [], later: [], done: [] }
  ;(stops || []).forEach(s => {
    if (!isTripStop(s)) by.plain.push(s)
    else if (isClosed(s.status)) by.done.push(s)
    else if (s.status !== 'pending') by.today.push(s) // started: today's work whatever its plan day
    else if (!s.planDate) by.pending.push(s)
    else if (s.planDate === today) by.today.push(s)
    else if (s.planDate < today) by.carried.push(s)
    else by.later.push(s)
  })
  return [
    { key: 'plain', title: 'งานวันนี้', stops: by.plain },
    { key: 'carried', title: 'ค้างจากวันก่อน', stops: by.carried },
    { key: 'today', title: 'แผนวันนี้', stops: by.today },
    { key: 'pending', title: 'ยังไม่ส่ง', stops: by.pending },
    { key: 'later', title: 'แผนวันถัดไป', stops: by.later },
    { key: 'done', title: 'เสร็จแล้ว', stops: by.done, collapsed: true }
  ].filter(x => x.stops.length)
}

// A trip's stops on one of its days, with ETAs for today's work only. Stops
// closed today lead (in the order they closed) so planStops re-anchors on the
// latest; then the work for today -- the plan's day and anything carried over
// for an up-country trip, everything pending for a route without a plan. The
// chain starts where the truck last finished (it slept there), not at the
// depot. Every other stop keeps its location and duration but no time.
function planTripDay (stops, today) {
  const list = stops || []
  const closedAt = s => {
    const step = (s.steps || {})[s.status === 'failed' ? REJECTED : 'finished']
    return (step && Date.parse(step.atDevice)) || 0
  }
  const closed = list.filter(s => isClosed(s.status)).sort((a, b) => closedAt(a) - closedAt(b))
  const doneToday = closed.filter(s => closedDay(s) === today)
  const work = list.filter(s => !isClosed(s.status) && (s.status !== 'pending' || !s.planDate || s.planDate <= today))
  const before = closed.filter(s => closedDay(s) !== today).slice(-1)[0]
  const from = before ? locateStop(before) : null
  planStops(doneToday.concat(work), geo.isPoint(from) ? { from } : {})
  const planned = doneToday.concat(work)
  list.forEach(s => {
    if (planned.indexOf(s) !== -1) return
    s.location = locateStop(s)
    const install = installMinutesFor(s)
    s.installMinutes = install.minutes
    s.installSource = install.source
    if (isClosed(s.status)) {
      // Closed on an earlier day: what happened, never a forecast.
      s.eta = null; s.etaClock = ''; s.etaSource = 'actual'
      s.actualFinish = clockFromISO(((s.steps || {})[s.status === 'failed' ? REJECTED : 'finished'] || {}).atDevice)
    } else {
      s.eta = null; s.etaClock = ''; s.etaSource = null
    }
  })
  return list
}
// The distinct SOs a stop delivers, in line order. An RT-only row carries its RT
// as soNo, so a line whose soNo is its own rtNo is a return, not a delivery.
function deliveredSos (stop) {
  const seen = []
  const lines = (stop && stop.lines) || []
  lines.forEach(line => {
    const so = line.soNo || ''
    if (!so || so === line.rtNo || seen.indexOf(so) !== -1) return
    seen.push(so)
  })
  return seen
}

// When a stamp was made, in epoch millis, or null. The server time wins; the
// device time covers a stamp the server has not filled in.
function stepMillis (step) {
  const s = step || {}
  const at = s.at
  let ms = null
  if (at && typeof at.toMillis === 'function') ms = at.toMillis()
  else if (at && Number.isFinite(at.seconds)) ms = at.seconds * 1000
  if (!Number.isFinite(ms)) ms = Date.parse(s.atDevice)
  return Number.isFinite(ms) ? ms : null
}

// When a stop was finished, as the Bangkok wall clock SAP is written with.
function deliveredAtText (step) {
  const ms = stepMillis(step)
  if (ms === null) return ''
  return new Date(ms + TZ_OFFSET_MINUTES * 60000).toISOString().slice(0, 19).replace('T', ' ')
}

// How long an owed delivered time is still worth writing: the Shopee auto-ship
// cron reads only the last 3 days of stamps, so a later write would ship nothing.
const SAP_DELIVERED_DAYS = 3

// True when an owed stop is past that window, measured from its FINISHED stamp --
// not its deliveryDate, which on a multi-day trip is the departure day, so a stop
// finished on day 4 would be dropped the moment it was owed. A stop with no
// readable finish time can never be written (deliveredAtText is ''), so it is
// past the window too.
function sapDeliveredExpired (stop, nowMs) {
  const ms = stepMillis(stop && stop.steps && stop.steps.finished)
  if (ms === null) return true
  return nowMs - ms > SAP_DELIVERED_DAYS * 24 * 60 * 60000
}

// The ASMX connections to try for a SAP write: the caller's selected one first,
// then the other of the two internet connections. Connection 3 (the office LAN
// path) is the same backend as Connection 2, so its fallback is Connection 1.
function sapConnections (selected, webPath, webPath2) {
  const first = selected || webPath
  const other = first === webPath ? webPath2 : webPath
  return other && other !== first ? [first, other] : [first]
}

// True when a finished stop still owes its SOs to SAP: finished (not rejected),
// the write marked pending, and at least one real SO to write.
function owesSapDelivered (stop) {
  const s = stop || {}
  const finished = !!(s.steps && s.steps.finished)
  const pending = !!(s.sapDelivered && s.sapDelivered.pending === true)
  return finished && pending && deliveredSos(s).length > 0
}

module.exports = {
  STEPS,
  STATUSES,
  REJECTED,
  isClosed,
  awaitingClose,
  deliveredSos,
  deliveredAtText,
  owesSapDelivered,
  SAP_DELIVERED_DAYS,
  sapDeliveredExpired,
  sapConnections,
  safeIdPart,
  stopKeyFor,
  stopIdFor,
  sortableTime,
  promisedWindow,
  repairClaimText,
  minutesFromISO,
  clockFromISO,
  groupStops,
  groupStopsByDate,
  isTripStop,
  tripInfo,
  closedDay,
  openTripKeys,
  tripSections,
  planTripDay,
  productsFor,
  loadList,
  statusFor,
  deleteGuard,
  deleteGuardNote,
  nextStep,
  canStamp,
  DEFAULT_START,
  MIN_LEG_MINUTES,
  locateStop,
  installMinutesFor,
  lineRef,
  stampLabel,
  planStops,
  mapsUrl
}
